import type { Layer } from '@geosoft/viewer-sdk';
import {
  isLayerExpiredCredentials,
  isLayerLowAccuracy,
  isLayerPartialLoading,
  isLayerStale,
} from '@/components/layers/layerModel';

export interface MapStateFlags {
  empty: boolean;
  offline: boolean;
  stale: boolean;
  lowAccuracy: boolean;
  mixedCrs: boolean;
  permissionDenied: boolean;
  expiredCredentials: boolean;
  partialLoading: boolean;
}

export interface PseudoAsset {
  id: string;
  label: string;
  layerId: string;
  point: [number, number];
  kind: Layer['kind'];
}

export function visibleLayers(catalog: Layer[], visibleIds: Set<string>): Layer[] {
  return catalog.filter((layer) => visibleIds.has(layer.id));
}

export function combinedBbox(layers: Layer[]): [number, number, number, number] | null {
  if (layers.length === 0) return null;
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const layer of layers) {
    const [layerMinX, layerMinY, layerMaxX, layerMaxY] = layer.bbox;
    minX = Math.min(minX, layerMinX);
    minY = Math.min(minY, layerMinY);
    maxX = Math.max(maxX, layerMaxX);
    maxY = Math.max(maxY, layerMaxY);
  }
  return [minX, minY, maxX, maxY];
}

export function bboxToCamera(
  bbox: [number, number, number, number] | null,
): { lonLat: [number, number]; zoom?: number } | null {
  if (!bbox) return null;
  const [minX, minY, maxX, maxY] = bbox;
  const lonLat: [number, number] = [(minX + maxX) / 2, (minY + maxY) / 2];
  const span = Math.max(maxX - minX, maxY - minY, 0.0001);
  const zoom = Math.max(2, Math.min(18, Math.log2(360 / span)));
  return { lonLat, zoom };
}

export function deriveMapStateFlags(
  layers: Layer[],
  permissionDeniedMessage: string | null,
): MapStateFlags {
  const now = new Date();
  const offline = typeof navigator !== 'undefined' ? !navigator.onLine : false;
  const stale = layers.some((layer) => isLayerStale(layer, now));
  const lowAccuracy = layers.some((layer) => isLayerLowAccuracy(layer));
  const expiredCredentials = layers.some((layer) => isLayerExpiredCredentials(layer));
  const partialLoading = layers.some((layer) => isLayerPartialLoading(layer));
  const mixedCrs = new Set(layers.map((layer) => layer.crs)).size > 1;
  return {
    empty: layers.length === 0,
    offline,
    stale,
    lowAccuracy,
    mixedCrs,
    permissionDenied: Boolean(permissionDeniedMessage),
    expiredCredentials,
    partialLoading,
  };
}

export function pseudoAssets(layers: Layer[]): PseudoAsset[] {
  return layers.map((layer) => {
    const [minX, minY, maxX, maxY] = layer.bbox;
    return {
      id: `asset-${layer.id}`,
      label: layer.label ?? layer.id,
      layerId: layer.id,
      point: [(minX + maxX) / 2, (minY + maxY) / 2],
      kind: layer.kind,
    };
  });
}

export interface AssetCluster {
  id: string;
  count: number;
  assets: PseudoAsset[];
}

export function clusterPseudoAssets(assets: PseudoAsset[]): AssetCluster[] {
  const clusters = new Map<string, PseudoAsset[]>();
  for (const asset of assets) {
    const key = `${Math.round(asset.point[0] * 10)}:${Math.round(asset.point[1] * 10)}`;
    const bucket = clusters.get(key);
    if (bucket) bucket.push(asset);
    else clusters.set(key, [asset]);
  }
  return [...clusters.entries()].map(([id, clusterAssets]) => ({
    id,
    count: clusterAssets.length,
    assets: clusterAssets,
  }));
}
